<template>
  <div>
    <transition name="group-actions-slide">
      <div v-if="isVisible" class="group-actions-panel" :style="panelStyle">
        <div class="group-actions-content">
          <div class="group-actions-info">
            <span class="group-actions-count">{{ selectedLabel }}</span>
            <button v-if="showClear" class="group-actions-clear-link" type="button" @click="clearSelection">
              {{ $t("common.clear_selection") }}
            </button>
          </div>
          <div class="group-actions-buttons">
            <slot />
            <button
              v-if="showDeleteAction"
              type="button"
              class="group-actions-button group-actions-button--danger"
              :disabled="isDeleteDisabled"
              @click="handleDeleteAction"
            >
              {{ deleteButtonLabel }}
            </button>
          </div>
        </div>
      </div>
    </transition>
    <ConfirmDxPopup
      :show="confirmDialog.open"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      @confirm="confirmDialogConfirm"
      @close="confirmDialogClose"
    />
  </div>
</template>

<script>
import ConfirmDxPopup from "@/components/Modals/ConfirmDxPopup.vue";
import { errorMessage } from "@/helpers/common";
import { bulkAction } from "@/helpers/generalActions";

export default {
  name: "GridGroupActions",
  components: { ConfirmDxPopup },
  props: {
    gridRef: {
      type: Object,
      default: null,
    },
    selectedCount: {
      type: Number,
      default: 0,
    },
    visible: {
      type: Boolean,
      default: null,
    },
    width: {
      type: Number,
      default: null,
    },
    showClear: {
      type: Boolean,
      default: true,
    },
    showDeleteAction: {
      type: Boolean,
      default: false,
    },
    deleteEndpoint: {
      type: String,
      default: null,
    },
    deleteAction: {
      type: String,
      default: "delete",
    },
    deleteHandler: {
      type: Function,
      default: null,
    },
    deleteRowsByUuid: {
      type: Object,
      default: () => ({}),
    },
    allowDeleting: {
      type: Function,
      default: null,
    },
    deleteUuids: {
      type: Array,
      default: () => [],
    },
    deleteDisabled: {
      type: Boolean,
      default: false,
    },
    height: {
      type: Number,
      default: 72,
    },
  },
  data() {
    return {
      confirmDialog: {
        open: false,
        title: "",
        message: "",
        resolve: null,
        reject: null,
      },
      pendingBulkAction: null,
      isDeleting: false,
    };
  },
  computed: {
    isVisible() {
      if (this.visible === null) {
        return this.selectedCount > 0;
      }
      return this.visible;
    },
    hasDeleteConfiguration() {
      return (this.deleteHandler instanceof Function) || (typeof this.deleteEndpoint === "string" && this.deleteEndpoint.trim().length > 0);
    },
    selectedLabel() {
      return this.$t("common.selected_rows", { count: this.selectedCount });
    },
    panelStyle() {
      const style = {
        height: `${this.height}px`,
      };
      if (this.width) {
        style.maxWidth = `${this.width}px`;
      }
      return style;
    },
    isDeleteDisabled() {
      return (
        this.deleteDisabled ||
        this.isDeleting ||
        !this.hasDeleteConfiguration ||
        !Array.isArray(this.deleteUuids) ||
        this.deleteUuids.length === 0
      );
    },
    deleteButtonLabel() {
      return this.$t("common.delete");
    },
  },
  methods: {
    runConfirmedAction(executor, config = {}) {
      const { confirmMessage, confirmTitle } = config;
      if (confirmMessage) {
        return new Promise((resolve, reject) => {
          this.pendingBulkAction = {
            executor,
            resolve,
            reject,
          };
          this.confirmDialog = {
            open: true,
            title: confirmTitle || this.$t("common.confirm_delete_title"),
            message: confirmMessage,
            resolve,
            reject,
          };
        });
      }

      return executor();
    },
    runBulkAction(config = {}) {
      const { confirmMessage, confirmTitle, ...rest } = config;
      return this.runConfirmedAction(() => bulkAction(this, rest), { confirmMessage, confirmTitle });
    },
    confirmDialogConfirm() {
      const pending = this.pendingBulkAction;
      this.confirmDialog = { open: false, title: "", message: "", resolve: null, reject: null };
      this.pendingBulkAction = null;
      if (!pending || !(pending.executor instanceof Function)) {
        return;
      }

      Promise.resolve(pending.executor())
        .then((result) => {
          if (pending.resolve instanceof Function) {
            pending.resolve(result);
          }
        })
        .catch((error) => {
          if (pending.reject instanceof Function) {
            pending.reject(error);
          }
        });
    },
    confirmDialogClose() {
      const pending = this.pendingBulkAction;
      this.confirmDialog = { open: false, title: "", message: "", resolve: null, reject: null };
      this.pendingBulkAction = null;
      if (pending && pending.resolve instanceof Function) {
        pending.resolve({ cancelled: true });
      }
    },
    clearSelection() {
      const instance = this.gridRef && this.gridRef.instance ? this.gridRef.instance : null;
      if (instance && typeof instance.clearSelection === "function") {
        instance.clearSelection();
      }
      this.$emit("cleared");
    },
    getDeleteEligibleUuids() {
      const uuids = Array.isArray(this.deleteUuids) ? this.deleteUuids : [];
      if (!(this.allowDeleting instanceof Function)) {
        return uuids;
      }

      return uuids.filter((uuid) => {
        const row = this.deleteRowsByUuid ? this.deleteRowsByUuid[uuid] : null;
        if (!row) {
          return false;
        }

        return this.allowDeleting(row);
      });
    },
    async handleDeleteAction() {
      if (this.isDeleteDisabled) {
        if (!this.hasDeleteConfiguration) {
          errorMessage(this, this.$t("common.no_available_function"));
        }
        return;
      }

      const eligibleDeleteUuids = this.getDeleteEligibleUuids();
      if (!eligibleDeleteUuids.length) {
        errorMessage(this, this.$t("common.no_available_function"));
        return;
      }

      this.isDeleting = true;
      try {
        const result = this.deleteHandler instanceof Function
          ? await this.runConfirmedAction(() => this.deleteHandler(eligibleDeleteUuids), {
              confirmTitle: this.$t("bulk_actions.confirm_title"),
              confirmMessage: this.$t("bulk_actions.confirm_delete", { count: eligibleDeleteUuids.length }),
            })
          : await this.runBulkAction({
              endpoint: this.deleteEndpoint,
              action: this.deleteAction,
              uuids: eligibleDeleteUuids,
              confirmTitle: this.$t("bulk_actions.confirm_title"),
              confirmMessage: this.$t("bulk_actions.confirm_delete", { count: eligibleDeleteUuids.length }),
              emptyMessage: this.$t("bulk_actions.no_selection"),
              successMessageBuilder: (resp) => {
                return this.$t("bulk_actions.deleted_summary", {
                  deleted: resp?.data?.deleted ?? resp?.deleted ?? 0,
                });
              },
            });

        if (result && result.cancelled) {
          return;
        }

        this.clearSelection();
        this.$emit("delete-success", result);
      } finally {
        this.isDeleting = false;
      }
    },
    updateGridHeightCssVar(height) {
      const gridEl = this.gridRef && this.gridRef.$el ? this.gridRef.$el : null;
      if (!gridEl || typeof gridEl.style?.setProperty !== "function") return;
      gridEl.style.setProperty("--grid-group-actions-height", `${height}px`);
    },
  },
  watch: {
    isVisible: {
      immediate: true,
      handler(newVal) {
        const currentHeight = newVal ? this.height : 0;
        this.updateGridHeightCssVar(currentHeight);
        this.$emit("visibility-change", currentHeight);
      },
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/global/colors.scss";

.group-actions-panel {
  position: sticky;
  bottom: 0;
  width: 100%;
  background: #fff;
  border-top: 1px solid $gray-20;
  box-shadow: 0 -8px 20px rgba(0, 0, 0, 0.08);
  z-index: 10;
  padding: 12px 16px;
  min-height: 56px;
}

.group-actions-content {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
}

.group-actions-info {
  font-weight: 600;
  color: $gray-80;
  display: flex;
  flex-direction: column;
  gap: 4px;
  order: 1;
}

.group-actions-count {
  color: $gray-90;
}

.group-actions-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  order: 2;
  padding-left: 40px;
}

.group-actions-clear-link {
  background: transparent;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  color: $selection-color;
  cursor: pointer;
  text-align: left;
  text-decoration: underline;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.group-actions-clear-link:hover,
.group-actions-clear-link:focus-visible {
  color: $brand-dark;
  outline: none;
}

.group-actions-button--danger {
  //border-color: $danger-color;
  color: $danger-color;
}

.group-actions-slide-enter-active,
.group-actions-slide-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.group-actions-slide-enter,
.group-actions-slide-leave-to {
  transform: translateY(12px);
  opacity: 0;
}
</style>

<style lang="scss">
@import "@/assets/scss/global/colors.scss";

.group-actions-button {
  min-height: 36px;
  padding: 0 14px;
  border-radius: 6px;
  border: 1px solid $gray-30;
  background: #fff;
  color: $gray-90;
  font-size: 14px;
  font-weight: 600;
}

.group-actions-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
